/**
 * Date Range Filter Panel
 * Story 4.3: Implement Date Range Filter
 * AC-4.3.1: Date range with start/end pickers and presets
 * AC-4.3.5: Preset selection updates both date pickers visually
 */
import React, { useCallback, useMemo } from 'react';
import {
  calculatePresetDates,
  getDateRangePresets,
} from '../utils/dateRangeFilter';
import type { DateRangeFilter, DateRangePreset, GridColumn } from '../types/gridTypes';

interface DateRangeFilterPanelProps {
  columns: GridColumn[];
  filter: DateRangeFilter | null;
  onFilterChange: (filter: DateRangeFilter | null) => void;
}

export function DateRangeFilterPanel({
  columns,
  filter,
  onFilterChange,
}: DateRangeFilterPanelProps) {
  // Get date columns only
  const dateColumns = useMemo(
    () => columns.filter(c => c.type === 'date'),
    [columns]
  );

  const presets = getDateRangePresets();

  // Handle field selection
  const handleFieldChange = useCallback((fieldId: string) => {
    if (!fieldId) {
      onFilterChange(null);
      return;
    }
    // Create new filter with default preset
    const { startDate, endDate } = calculatePresetDates('this_month');
    onFilterChange({
      fieldId,
      preset: 'this_month',
      startDate,
      endDate,
    });
  }, [onFilterChange]);

  // Handle preset change (AC-4.3.5: updates both date pickers)
  const handlePresetChange = useCallback((preset: DateRangePreset) => {
    if (!filter) return;

    if (preset === 'custom') {
      onFilterChange({
        ...filter,
        preset: 'custom',
      });
    } else {
      const { startDate, endDate } = calculatePresetDates(preset);
      onFilterChange({
        ...filter,
        preset,
        startDate,
        endDate,
      });
    }
  }, [filter, onFilterChange]);

  // Handle manual date changes
  const handleStartDateChange = useCallback((startDate: string) => {
    if (!filter) return;
    onFilterChange({
      ...filter,
      preset: 'custom',
      startDate: startDate || null,
    });
  }, [filter, onFilterChange]);

  const handleEndDateChange = useCallback((endDate: string) => {
    if (!filter) return;
    onFilterChange({
      ...filter,
      preset: 'custom',
      endDate: endDate || null,
    });
  }, [filter, onFilterChange]);

  // Clear filter
  const handleClear = useCallback(() => {
    onFilterChange(null);
  }, [onFilterChange]);

  return (
    <div className="date-range-filter-panel p-4 space-y-4">
      <div className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Date Range Filter
      </div>

      {/* Field selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Field</label>
        <select
          value={filter?.fieldId || ''}
          onChange={e => handleFieldChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Select a date field...</option>
          {dateColumns.map(col => (
            <option key={col.id} value={col.id}>
              {col.label}
            </option>
          ))}
        </select>
      </div>

      {filter && (
        <>
          {/* Preset selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Quick Preset</label>
            <select
              value={filter.preset}
              onChange={e => handlePresetChange(e.target.value as DateRangePreset)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {presets.map(p => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
              <input
                type="date"
                value={filter.startDate || ''}
                onChange={e => handleStartDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
              <input
                type="date"
                value={filter.endDate || ''}
                onChange={e => handleEndDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Clear button */}
          <button
            onClick={handleClear}
            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            Clear Date Range Filter
          </button>
        </>
      )}

      {dateColumns.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No date fields available in this sheet.
        </div>
      )}
    </div>
  );
}
